import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { BASE_URL, CDN_URL, EMP_PREFIX, UPLOAD_URL } from "../config/apiConfig";
import { useAuth } from "./AuthContext";
// CHANGED: Removed unused import `{ type } from "@testing-library/user-event/dist/type"` — not used anywhere in this file.

const EmployeesContext = createContext();

export const useEmployee = () => useContext(EmployeesContext);

// CHdddsdwwwdffrggANGED: Added a safe JSON parsing helper to prevent "Unexpected token '<'" crashes.
// This guards against cases where the backend returns an HTML error page (404/500/proxy errors)
// or an empty body instead of valid JSON, which previously caused res.json() to throw.
const safeParseJSON = async (res) => {
    try {
        const text = await res.text();
        if (!text) {
            return { status: res.status, message: "Empty response from server", data: null };
        }
        try {
            return JSON.parse(text);
        } catch (parseErr) {
            return { status: res.status, message: "Invalid response format from server", data: null };
        }
    } catch (readErr) {
        return { status: res.status || 500, message: "Failed to read server response", data: null };
    }
};

export const EmployeesProvider = ({ children }) => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { isAuthenticated, token } = useAuth();

    const [ employeeStatuses, setEmployeeStatuses] = useState([]);
    const [ employeeTypes, setEmployeeTypes ] = useState([]);
    const [ workLocations, setWorkLocations ] = useState([]);
    
    const [statusLoading, setStatusLoading] = useState(false);
    const [statusError, setStatusError] = useState(null);
    const [typeLoading, setTypeLoading] = useState(false);
    const [typeError, setTypeError] = useState(null);
    const [workLocationLoading, setWorkLocationLoading] = useState(false);
    const [workLocationError, setWorkLocationError] = useState(null);

    const [allEmployees, setAllEmployees] = useState([]);
    const [allEmployeesLoading, setAllEmployeesLoading] = useState(false);
    const [allEmployeesError, setAllEmployeesError] = useState(null);

    // ✅ Fetch Employees Function
    // CHANGED: Removed toast.error calls for fetch failures/network errors (GET on page load).
    // setError state is preserved so the UI can still surface the error if needed.
    const fetchEmployees = async () => {
        setLoading(true);
        setError(null);
        try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}/employees`, {
            headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            },
        });

        const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
        if (res.ok && data.status === 200) {
            const formattedEmployees = data.data.map((emp, index) => ({
                id: emp.id
                    ? `${EMP_PREFIX}00${'1' + emp.id.toString().padStart(2, '0')}` // 00 + 1 + padded emp.id
                    : `${EMP_PREFIX}00${'1' + (index + 1).toString().padStart(2, '0')}`, // fallback    
                empId: emp.id,
                role_id: emp.role_id,
                first_name: emp.first_name || "N/A",
                last_name: emp.last_name || "N/A",
                email: emp.email || "N/A",
                phone: emp.phone || "",
                gender: emp.gender || "N/A",
                department: emp.dep_name || "Unassigned",
                department_id:emp.department_id,
                reporting_manager:emp.reporting_manager,
                designation: emp.deg_name || "N/A",
                designation_id: emp.designation_id || "N/A",
                address:emp.address || "N/A",
                postcode:emp.postcode || "N/A",
                bank:emp.bank || "N/A",
                ifsc:emp.ifsc || "N/A",
                acc_no:emp.acc_no || "N/A",
                pan_no:emp.pan_no || "N/A",
                uan_no:emp.uan_no || "N/A",
                pf_no:emp.pf_no || "N/A",
                role_name:emp.role_name || "N/A",
                salary:emp.salary || "N/A",
                dob:emp.dob || "N/A",
                status: emp.emp_status || "Pending",
                type: emp.emp_type || "N/A",
                workLocation: emp.work_location || "N/A",
                status_id : emp.emp_status_id || null,
                type_id : emp.emp_type_id || null,
                location_id : emp.work_location_id || null,
                joiningDate: emp.joiningdate || "N/A",
                exit_date: emp.exit_date || "N/A",
                image: emp.image ? `${CDN_URL}/${emp.image}` : null,
                reporting_manager_name:emp.reporting_manager_name || "NONE",
                // add other fields as needed

            }));

            setEmployees(formattedEmployees || []);
            return { success: true, data: formattedEmployees };
        } else {
            setError(data.message || "Failed to fetch employees");
            // CHANGED: removed toast.error("Failed to fetch employees") — no toast on fetch failure
            return { success: false, message: data.message };
        }
        } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Network error. Please try again.");
        // CHANGED: removed toast.error("Network error. Please try again.") — no toast on network error
        return { success: false, message: "Network error. Please try again." };
        } finally {
        setLoading(false);
        }
    };

    // 🔹 Fetch employee by ID
    // CHANGED: Removed toast.error calls — this is a read/fetch operation.
    //
    // CHANGED: Wrapped in useCallback. Dashboard, Header and Profile each list
    // this function in a useEffect dependency array, so as a bare `const` its
    // identity changed on every provider re-render and all three effects
    // re-fired — one GET /users/{id} per component per render. Paired with the
    // old 5s permissions poll (which called setUser and re-rendered the whole
    // tree) this generated ~23k requests/day on its own. Empty dep array is
    // safe: the token is read from localStorage inside the call and BASE_URL is
    // a module constant, so nothing from the render scope is captured.
    const getEmployeeById = useCallback(async (id) => {
        try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}/users/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
        if (data.status === 200 && data.data.length > 0) {
            return data.data[0];
        }
        // CHANGED: removed toast.error(data.message || "Failed to fetch employee")
        return null;
        } catch (err) {
        // CHANGED: removed toast.error("Error fetching employee")
        return null;
        }
    }, []);


    // ✅ User action (Create) — toast notifications preserved
    const createEmployee = async (employeeData) => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${BASE_URL}/employees`, {
                method: "POST",
                headers: {
                    // "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                // body: JSON.stringify(employeeData),
                body: employeeData,
            });

            const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
            
            if (res.ok && data.status === 201) {
                await fetchEmployees(); // refresh list
                toast.success(data.message || "Employee created successfully"); // KEPT: user-initiated action
                return { success: true, message: data.message };
            } else {
                setError(data.message || "Failed to create employee");
                toast.error(data.message || "Failed to create employee"); // KEPT: user-initiated action
                return { success: false, message: data.message };
            }
        } catch (err) {
            console.error("Error creating employee:", err);
            setError("Network error. Please try again.");
            toast.error("Network error. Please try again."); // KEPT: user-initiated action
            return { success: false, message: "Network error. Please try again." };
        } finally {
            setLoading(false);
        }
    };

    // CHANGED: Removed toast.error calls — this is a fetch operation (page load / dropdown data).
    const fetchEmployeeStatuses = async () => {
        setStatusLoading(true);
        setStatusError(null);
        try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}/employee-status`, {
            headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            },
        });
        const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
        if (res.ok && data.status === 200) {
            setEmployeeStatuses(data.data || []);
            return { success: true, data: data.data };
        } else {
            setStatusError(data.message || "Failed to fetch employee status");
            // CHANGED: removed toast.error(data.message || "Failed to fetch employee status")
            return { success: false, message: data.message };
        }
        } catch (err) {
        console.error("Error fetching employee status:", err);
        setStatusError("Network error. Please try again.");
        // CHANGED: removed toast.error("Network error. Please try again.")
        return { success: false, message: "Network error. Please try again." };
        } finally {
        setStatusLoading(false);
        }
    };

    // CHANGED: Removed toast.error calls — this is a fetch operation (page load / dropdown data).
    const fetchEmployeeTypes = async () => {
        setTypeLoading(true);
        setTypeError(null);
        try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}/employee-types`, {
            headers: { 
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}` 
            },
        });
        const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
        if (res.ok && data.status === 200) {
            setEmployeeTypes(data.data || []);
            return { success: true, data: data.data };
        } else {
            setTypeError(data.message || "Failed to fetch employee types");
            // CHANGED: removed toast.error(data.message || "Failed to fetch employee types")
            return { success: false, message: data.message };
        }
        } catch (err) {
        console.error("Error fetching employee types:", err);
        setTypeError("Network error. Please try again.");
        // CHANGED: removed toast.error("Network error. Please try again.")
        return { success: false, message: "Network error. Please try again." };
        } finally {
        setTypeLoading(false);
        }
    };

    // CHANGED: Removed toast.error calls — this is a fetch operation (page load / dropdown data).
    const fetchWorkLocations = async () => {
        setWorkLocationLoading(true);
        setWorkLocationError(null);
        try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}/work-locations`, {
            headers: { 
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
            },
        });
        const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()
        if (res.ok && data.status === 200) {
            setWorkLocations(data.data || []);
            return { success: true, data: data.data };
        } else {
            setWorkLocationError(data.message || "Failed to fetch work locations");
            // CHANGED: removed toast.error(data.message || "Failed to fetch work locations")
            return { success: false, message: data.message };
        }
        } catch (err) {
        console.error("Error fetching work locations:", err);
        setWorkLocationError("Network error. Please try again.");
        // CHANGED: removed toast.error("Network error. Please try again.")
        return { success: false, message: "Network error. Please try again." };
        } finally {
        setWorkLocationLoading(false);
        }
    };

    // ✅ User action (Update) — toast notifications preserved
    const updateEmployee = async (id, updatedData) => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${BASE_URL}/employees/${id}`, {
                method: "POST", // Using POST with FormData. Could be PUT if backend supports multipart PUT
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: updatedData, // FormData
            });

            const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

            if (res.ok && data.status === 200) {
                await fetchEmployees(); // refresh list
                toast.success(data.message || "Employee updated successfully"); // KEPT: user-initiated action
                return { success: true, message: data.message };
            } else {
                setError(data.message || "Failed to update employee");
                toast.error(data.message || "Failed to update employee"); // KEPT: user-initiated action
                return { success: false, message: data.message };
            }
        } catch (err) {
            console.error("Error updating employee:", err);
            setError("Network error. Please try again.");
            toast.error("Network error. Please try again."); // KEPT: user-initiated action
            return { success: false, message: "Network error. Please try again." };
        } finally {
            setLoading(false);
        }
    };


    // ✅ User action (Delete) — toast notifications preserved
    const deleteEmployee = async (id, deleteAuditId) => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            const url = `${BASE_URL}/employees/${id}`;
            const bodyData = { delete_audit_id: deleteAuditId };

            const res = await fetch(url, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(bodyData),
            });

            const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

            if (res.ok && data.status === 200) {
                await fetchEmployees();
                toast.success(data.message || "Employee deleted successfully"); // KEPT: user-initiated action
                return { success: true, message: data.message };
            } else {
                setError(data.message || "Failed to delete employee");
                toast.error(data.message || "Failed to delete employee"); // KEPT: user-initiated action
                return { success: false, message: data.message };
            }
        } catch (err) {
            setError("Network error. Please try again.");
            toast.error("Network error. Please try again."); // KEPT: user-initiated action
            return { success: false, message: "Network error. Please try again." };
        } finally {
            setLoading(false);
        }
    };

    // CHANGED: Removed toast.error calls — this is a fetch operation (page load / dropdown data).
    const fetchAllEmployees = async () => {
        setAllEmployeesLoading(true);
        setAllEmployeesError(null);

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${BASE_URL}/allemployees`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

            if (res.ok && data.status === 200) {
                const formattedEmployees = (data.data || []).map((emp) => ({
                    id: Number(emp.id),
                    first_name: emp.first_name,
                    last_name: emp.last_name,
                    full_name: `${emp.first_name} ${emp.last_name}`.trim(),
                }));

                setAllEmployees(formattedEmployees);

                return {
                    success: true,
                    data: formattedEmployees,
                };
            } else {
                setAllEmployeesError(data.message || "Failed to fetch employees");
                // CHANGED: removed toast.error(data.message || "Failed to fetch employees")
                return {
                    success: false,
                    message: data.message,
                };
            }
        } catch (err) {
            console.error("Error fetching all employees:", err);
            setAllEmployeesError("Network error. Please try again.");
            // CHANGED: removed toast.error("Network error. Please try again.")
            return {
                success: false,
                message: "Network error. Please try again.",
            };
        } finally {
            setAllEmployeesLoading(false);
        }
    };

    useEffect(() => {
        if(!isAuthenticated) return;
        fetchEmployees();
        fetchAllEmployees();
        fetchEmployeeStatuses();
        fetchEmployeeTypes();
        fetchWorkLocations();
    }, [isAuthenticated, token]);


    const departmentPalette = [
        "#8b5cf6", "#facc15", "#3b82f6", "#f97316", "#22c55e", "#ec4899", "#10b981", "#60a5fa",
    ];

    const departmentChart = useMemo(() => {
        const total = employees.length || 1; // avoid divide by zero
        const counts = employees.reduce((acc, e) => {
        const key = e.department || "Unassigned";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
        }, {});

        return Object.entries(counts).map(([name, count], idx) => ({
        name,
        value: Math.round((count / total) * 100), // percentage for Pie
        count,                                     // keep raw count if you want labels
        color: departmentPalette[idx % departmentPalette.length],
        }));
    }, [employees]);


    return (
        <EmployeesContext.Provider
        value={{
            employees,
            loading,
            error,
            departmentChart,
            employeeStatuses,
            employeeTypes,
            workLocations,
            statusLoading,
            statusError,
            typeLoading,
            typeError,
            workLocationLoading,
            workLocationError,
            deleteEmployee,
            updateEmployee,
            fetchEmployeeTypes,
            fetchWorkLocations,
            createEmployee,
            getEmployeeById,
            fetchEmployeeStatuses,
            allEmployees,
            fetchAllEmployees,
        }}
        >
        {children}
        </EmployeesContext.Provider>
    );
};

export const useEmployees = () => useContext(EmployeesContext);